import React, { useMemo, useState } from 'react'
import { Avatar, Icon, Select, Spin } from 'antd'
import Flag from 'react-world-flags'
import _ from 'lodash'

import { Logo } from '..'
import { api } from '../../services'
import history from '../../history'
import { languages } from '../../constants'
import userDummyIcon from './images/user.svg'

const { Option } = Select

const getSmallProfileImage = image => {
  if (!image) return ''

  const { link, resizes } = image
  if (_.isEmpty(resizes)) return link
  return resizes.thumbnail
}

function MobileHeaderPanel({
  user,
  organizations,
  isFetchingOrganizations,
  headerLogo,
  lang,
  onLanguageChange,
  onClose,
  onToggleCenter,
  isShowingKnowledgeCenter,
  isJustSignedIn,
  canShowAssistance,
  onToggleAssistance,
  canShowShareBot,
  onToggleBot,
  t
}) {
  const [updatingUser, setUpdatingUser] = useState(false)

  const {
    activeOrg,
    organizationRows,
    orgId,
    orgLogo,
    picture,
    userName,
    userEmail,
    canManage
  } = useMemo(() => {
    const info = (user && user.info) || {}
    const rows =
      organizations && Array.isArray(organizations.rows)
        ? organizations.rows
        : []
    const activeOrg = rows.find(org => org.id === info.active_organization_id)
    const orgLogo =
      activeOrg && activeOrg.logo ? getSmallProfileImage(activeOrg.logo) : ''
    const picture = getSmallProfileImage(info.avatar) || userDummyIcon
    const hasSuperAdminAccess = info.isSuperAdmin || info.is_super_admin

    return {
      activeOrg,
      organizationRows: rows,
      orgId: info.active_organization_id,
      orgLogo,
      picture,
      userName: info.name || info.username || '',
      userEmail: info.email || '',
      canManage: hasSuperAdminAccess || info.isAdmin
    }
  }, [user, organizations])

  const workspaceName = activeOrg ? activeOrg.title : userName
  const workspaceType = activeOrg
    ? t('general:organization')
    : t('general:personal')

  const navigateTo = path => {
    history.push(path)
    onClose()
  }

  const handleSignOut = () => {
    api.user.signOut().then(() => {
      window.location.reload()
    })
  }

  const switchAccount = id => {
    const info = (user && user.info) || {}
    if (info.active_organization_id === id) {
      onClose()
      return
    }

    setUpdatingUser(true)
    api.user
      .updateUser(info.id, null, { active_organization_id: id })
      .then(() => {
        window.location.reload()
      })
  }

  const renderAvatar = (image, title, isOrg = false) => {
    if (image) {
      return <img src={image} alt="" />
    }

    if (title) {
      return (
        <span className="mobile-header-panel__avatar-fallback">
          {title.charAt(0).toUpperCase()}
        </span>
      )
    }

    return isOrg ? (
      <span className="mobile-header-panel__avatar-fallback">O</span>
    ) : (
      <Avatar size="large" icon="user" />
    )
  }

  return (
    <div className="mobile-header-panel">
      <div className="mobile-header-panel__topbar">
        <div className="mobile-header-panel__brand">
          <Logo color="white" logo={headerLogo} />
        </div>
        <button
          type="button"
          className="mobile-header-panel__close"
          aria-label="Close menu"
          onClick={onClose}
        >
          <Icon type="close" />
        </button>
      </div>

      <div className="mobile-header-panel__profile">
        <div className="mobile-header-panel__avatar">
          {renderAvatar(orgId && activeOrg ? orgLogo : picture, workspaceName)}
        </div>
        <div className="mobile-header-panel__identity">
          <span className="mobile-header-panel__eyebrow">{workspaceType}</span>
          <strong>{workspaceName}</strong>
          <span>{userEmail}</span>
        </div>
      </div>

      <div className="mobile-header-panel__quick-grid">
        {!isJustSignedIn && (
          <button type="button" onClick={onToggleCenter}>
            <Icon type="bulb" />
            <span>
              {isShowingKnowledgeCenter
                ? t('v3:learning_center')
                : t('v3:knowledge_center')}
            </span>
          </button>
        )}
        {canShowShareBot && (
          <button type="button" onClick={onToggleBot}>
            <Icon type="message" />
            <span>{t('v3:sharebot')}</span>
          </button>
        )}
        {canShowAssistance && (
          <button type="button" onClick={onToggleAssistance}>
            <Icon type="customer-service" />
            <span>{t('v4:sharelook_assistance')}</span>
          </button>
        )}
      </div>

      <div className="mobile-header-panel__section">
        <span className="mobile-header-panel__section-title">Account</span>
        <button
          type="button"
          className="mobile-header-panel__row"
          onClick={() => navigateTo('/account')}
        >
          <Icon type="user" />
          <span>{t('navigation:account')}</span>
          <Icon type="right" />
        </button>
        <button
          type="button"
          className="mobile-header-panel__row"
          onClick={() => navigateTo('/calendar')}
        >
          <Icon type="calendar" />
          <span>{t('v3:my_calendar')}</span>
          <Icon type="right" />
        </button>
        {canManage && (
          <button
            type="button"
            className="mobile-header-panel__row"
            onClick={() => navigateTo('/management')}
          >
            <Icon type="team" />
            <span>{t('navigation:organizations_users')}</span>
            <Icon type="right" />
          </button>
        )}
      </div>

      <div className="mobile-header-panel__section">
        <span className="mobile-header-panel__section-title">
          {t('v4:selected_language')}
        </span>
        <div
          id="mobile_language_select"
          className="mobile-header-panel__language"
        >
          <Select
            size="large"
            value={lang}
            onChange={onLanguageChange}
            suffixIcon={<Icon type="down" className="ssl-language-arrow" />}
            dropdownClassName="ssl-language-dropdown"
            getPopupContainer={() =>
              document.getElementById('mobile_language_select')
            }
          >
            {languages.map(item => (
              <Option key={item.key} value={item.key}>
                <div
                  className={`ssl-flag-wrap ${
                    item.key === 'traditional_chinese'
                      ? 'ssl-flag-wrap_long'
                      : ''
                  }`}
                >
                  <Flag code={item.code.includes('en') ? 'us' : item.code} />
                  <span className="ssl-language-name">{item.name}</span>
                </div>
              </Option>
            ))}
          </Select>
        </div>
      </div>

      <Spin
        spinning={updatingUser || isFetchingOrganizations}
        tip={updatingUser ? t('v4:switching_organization') : ''}
      >
        <div className="mobile-header-panel__section">
          <span className="mobile-header-panel__section-title">
            Switch workspace
          </span>
          <div className="mobile-header-panel__workspaces">
            {organizationRows.map(({ id, title, logo }) => {
              const logoImage = logo ? getSmallProfileImage(logo) : ''
              const selected = orgId === id

              return (
                <button
                  key={id}
                  type="button"
                  className={`mobile-header-panel__workspace ${
                    selected ? 'is-selected' : ''
                  }`}
                  onClick={() => switchAccount(id)}
                >
                  <span className="mobile-header-panel__workspace-avatar">
                    {renderAvatar(logoImage, title, true)}
                  </span>
                  <span className="mobile-header-panel__workspace-text">
                    <strong>{title}</strong>
                    <span>
                      {selected ? 'Current workspace' : t('v4:switch')}
                    </span>
                  </span>
                  {selected && <Icon type="check" />}
                </button>
              )
            })}

            <button
              type="button"
              className={`mobile-header-panel__workspace ${
                !orgId ? 'is-selected' : ''
              }`}
              onClick={() => switchAccount(null)}
            >
              <span className="mobile-header-panel__workspace-avatar">
                {renderAvatar(picture, userName)}
              </span>
              <span className="mobile-header-panel__workspace-text">
                <strong>{userName}</strong>
                <span>
                  {!orgId ? 'Current workspace' : t('general:personal')}
                </span>
              </span>
              {!orgId && <Icon type="check" />}
            </button>
          </div>
        </div>
      </Spin>

      <button
        type="button"
        className="mobile-header-panel__signout"
        onClick={handleSignOut}
      >
        <Icon type="logout" />
        <span>{t('navigation:logout')}</span>
      </button>
    </div>
  )
}

export default MobileHeaderPanel
